
import { defineComponent, ref, PropType, computed } from "vue";

import EasyDrop from "@/components/EasyDrop.vue";

import { SearchRoom } from "@/types/models";
import { SearchRoomChildParams } from "@/types/paramModels";
import { useStore } from "vuex";

export default defineComponent({
  name: "SearchTravellers",
  emits: ["searchTravel"],
  components: {
    EasyDrop
  },
  props: {
    rooms: {
      type: Array as PropType<Array<SearchRoom>>,
      required: true
    },
    pageTexts: {
      type: Object,
      required: true
    },
    isHotelApp: {
      type: Boolean
    }
  },
  setup(_props, { emit }) {
    const store = useStore();
    const easyDrop = ref();

    const adultsCount = computed(() => _props.rooms.reduce((acc, { adults }) => acc + adults.length, 0));
    const childrenCount = computed(() => _props.rooms.reduce((acc, { children }) => acc + children.length, 0));
    const paxCount = computed(() => adultsCount.value + childrenCount.value);
    const displayRooms = computed(() => _props.rooms);

    const childAgeMissing = ref(false);

    const removeAdult = (index: number) => {
      store.dispatch("TravelSearchModule/removeSearchTravelRequestAdult", index);
    };

    const addAdult = (index: number) => {
      store.dispatch("TravelSearchModule/addSearchTravelRequestAdult", index);
    };

    const removeChild = (index: number) => {
      store.dispatch("TravelSearchModule/removeSearchTravelRequestChild", index);
      if (allChildrenHasAge.value) childAgeMissing.value = false;
    };

    const addChild = (index: number) => {
      store.dispatch("TravelSearchModule/addSearchTravelRequestChild", index);
    };

    const updateChildAge = (roomIndex: number, childIndex: number, age: number) => {
      const params = { 'roomIndex': roomIndex, 'childIndex': childIndex, 'age': age } as SearchRoomChildParams;
      store.dispatch("TravelSearchModule/updateSearchTravelRequestChildAge", params);

      const buttonId = "btnRoom" + roomIndex + "Child" + childIndex;
      const childButton = document.getElementById(buttonId);
      if (childButton)
        childButton.removeAttribute('data-error');

      if (allChildrenHasAge.value) childAgeMissing.value = false;
    }

    const addRoom = () => {
      store.dispatch("TravelSearchModule/addSearchTravelRequestRoom");
    }

    const deleteRoom = (index: number) => {
      store.dispatch("TravelSearchModule/deleteSearchTravelRequestRoom", index);
    }

    const adultSinglePlural = (count: number) => {
      return count > 1 ? _props.pageTexts['searchbarPersonPickerLabelAdults'] : _props.pageTexts['searchbarPersonPickerLabelAdult'];
    }

    const childSinglePlural = (count: number) => {
      return count > 1 ? _props.pageTexts['searchbarPersonPickerLabelChildren'] : _props.pageTexts['searchbarPersonPickerLabelChild'];
    }

    const allChildrenHasAge = computed(() => {
      for (const room of _props.rooms) {
        for (const child of room.children) {
          if (child.age === null) return false;
        }
      }
      return true;
    });

    const paxLimit = () => {
      if (paxCount.value > 9) return false;
      return true;
    };

    const searchTravel = () => {
      emit("searchTravel");
    }

    const validateChildrenAges = () => {
      childAgeMissing.value = !allChildrenHasAge.value;
      for (const [roomIndex, room] of _props.rooms.entries()) {
        for (const [childIndex, child] of room.children.entries()) {
          if (!child.age) {
            const buttonId = "btnRoom" + roomIndex + "Child" + childIndex;
            const childButton = document.getElementById(buttonId);
            if (childButton)
              childButton.dataset.error = "true";
          }
        }
      }
    }

    const closeDropdown = () => {
      if (allChildrenHasAge.value && easyDrop.value && paxLimit())
        easyDrop.value.toggleDropdown();

      if (!allChildrenHasAge.value) {
        childAgeMissing.value = true
      }

    }



    return {
      easyDrop,
      closeDropdown,
      adultsCount,
      childrenCount,
      displayRooms,
      removeAdult,
      addAdult,
      removeChild,
      addChild,
      updateChildAge,
      addRoom,
      deleteRoom,
      adultSinglePlural,
      childSinglePlural,
      childAgeMissing,
      allChildrenHasAge,
      validateChildrenAges,
      searchTravel,
      paxCount,
      paxLimit,
    };
  },
});
